import type { Role } from './domain.js';

/**
 * Authorisation.
 *
 * A two-tier model — administrator versus ordinary employee — cannot express "a technician
 * may record their own hours and take a payment at the door of the job they are standing at,
 * but may not see the company's revenue, what colleagues earn, or what anything cost us".
 *
 * So capabilities are explicit and the technician role is defined by what it CANNOT do.
 */

export const CAPABILITIES = [
  // --- work ---
  'job.read.assigned',        // only jobs I am assigned to
  'job.read.all',             // every job in the tenant
  'job.write',                // create, edit, move stage
  'job.assign',               // change who is on a job
  'job.complete',             // mark work done

  // --- customers ---
  'customer.read.related',    // only customers attached to jobs I am assigned to
  'customer.read.all',
  'customer.write',
  'customer.contact',         // see phone numbers and email addresses

  // --- money: deliberately fine-grained ---
  'billing.read.job',         // see the amount owing on a job I am working on
  'billing.read.all',         // browse all quotes and invoices
  'billing.write',            // create and edit quotes and invoices
  'billing.cost.read',        // see cost prices and margin — commercially sensitive
  /*
   * Draft a quote or an invoice on a job I am assigned to, priced from the catalogue, and
   * never issue or send one.
   *
   * An explicit addition rather than an inheritance: the imported permissions denied every
   * field employee the creation of a quote, without exception. But `sales` is the largest
   * single call reason in the history, and a technician who has just opened a unit is the
   * best-placed person in the company to quote the next job. So the business opts in, on the
   * role, and issuing stays with the office.
   */
  'billing.draft.own',
  'payment.collect',          // take a payment on site
  'payment.read.all',         // see all payments received
  'payment.refund',

  // --- time ---
  'time.write.own',           // clock my own hours
  /*
   * Log hours for the crew on a visit I lead — including a helper without a phone, who is
   * otherwise invisible. A stop is shared by a crew: 576 imported visits had two people on
   * them, 56 had three, 13 had five.
   */
  'time.write.crew',
  'time.read.own',
  'time.read.all',            // see everyone's hours
  'time.approve',             // sign off timesheets for payroll
  'time.rate.read',           // see hourly rates (i.e. what people are paid)

  // --- equipment ---
  /*
   * Create or update the equipment record on a dwelling I am working at, from a nameplate
   * scan. Scoped to related work for the same reason `customer.read.related` is: a technician
   * may write about the unit they are standing in front of, not about the fleet.
   */
  'asset.write.related',

  // --- communication ---
  'thread.read.related',
  'thread.read.all',
  'thread.write',
  'call.read.all',
  'call.place',

  // --- oversight ---
  'report.read',              // dashboards, revenue, campaign attribution
  'settings.read',
  'settings.write',
  'user.manage',
  'tenant.manage',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * What each role can do.
 *
 * The technician set is the important one. A technician CAN do their whole job offline:
 * see their assigned work, reach the customer, log hours, photograph what they found,
 * message the office, and take cash or a card at the door. A technician CANNOT see the
 * business's finances, what anything cost, what colleagues earn, or work they are not on.
 */
const TECHNICIAN: readonly Capability[] = [
  'job.read.assigned',
  'job.complete',
  'customer.read.related',
  'customer.contact',
  // Sees what THIS customer owes on THIS job, because they may be asked at the door and
  // may need to collect it. Not the company's books.
  'billing.read.job',
  'payment.collect',
  'time.write.own',
  'time.read.own',
  // Creates the equipment record by scanning its nameplate. The record is the point of the
  // scan; a form nobody fills in is not.
  'asset.write.related',
  'thread.read.related',
  'thread.write',
];

/**
 * A senior technician: everything a technician has, plus the paperwork around their own job.
 *
 * Derived from what the business actually granted these people one by one — quotes, invoices,
 * the routes for the day, the timesheets tab — and nothing beyond it. Notably still absent:
 * cost prices, profits, other people's rates, and creating records. Those were denied to every
 * field employee without exception, which is a clear enough policy to encode.
 */
const LEAD_TECHNICIAN: readonly Capability[] = [
  ...TECHNICIAN,
  // Sees the paperwork for the work, so they can answer a customer holding the quote.
  'billing.read.all',
  // Sees the day's plan, including who else is out — needed to hand a job over.
  'job.read.all',
  // Drafts the quote at the door, from the stop. Issuing is still the office's.
  'billing.draft.own',
  // Starts the clock for everyone present at *arrived*, the helper without a phone included.
  'time.write.crew',
  'time.read.all',
  'call.read.all',
  'thread.read.all',
];

const DISPATCHER: readonly Capability[] = [
  'job.read.all',
  'job.write',
  'job.assign',
  'job.complete',
  'customer.read.all',
  'customer.write',
  'customer.contact',
  'billing.read.job',
  'billing.read.all',
  'time.read.all',
  'thread.read.all',
  'thread.write',
  'call.read.all',
  'call.place',
  'report.read',
  'settings.read',
];

const ACCOUNTANT: readonly Capability[] = [
  'job.read.all',
  'customer.read.all',
  'customer.contact',
  'billing.read.job',
  'billing.read.all',
  'billing.write',
  'billing.cost.read',
  'payment.read.all',
  'payment.collect',
  'payment.refund',
  'time.read.all',
  'time.approve',
  'time.rate.read',
  'report.read',
  'settings.read',
  'thread.read.all',
];

const ADMIN: readonly Capability[] = [...CAPABILITIES];

/**
 * The capability sets each built-in role is seeded with.
 *
 * These are a **starting point, not the model.** The model is the `roles` table: a business
 * edits these, adds its own, and the application reads capabilities from the row. They stay
 * here so a new business gets something sensible on day one, and so the seed is reviewable in
 * one place rather than buried in a migration.
 */
export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  owner: ADMIN,
  admin: ADMIN,
  dispatcher: DISPATCHER,
  accountant: ACCOUNTANT,
  lead_technician: LEAD_TECHNICIAN,
  technician: TECHNICIAN,
};

/**
 * The built-in roles, as they are created for a new business.
 *
 * `protected` marks the one role nobody may edit or delete. Without an immutable full-access
 * role, one bad save in the permission editor locks everybody out of their own system with no
 * way back in that does not involve a database console.
 */
export const ROLE_TEMPLATES: ReadonlyArray<{
  key: Role;
  name: { fr: string; en: string };
  description: { fr: string; en: string };
  capabilities: readonly Capability[];
  landing: 'portal' | 'field';
  position: number;
  protected: boolean;
}> = [
  {
    key: 'owner',
    name: { fr: 'Propriétaire', en: 'Owner' },
    description: {
      fr: 'Accès complet. Ce rôle ne peut pas être modifié ni supprimé.',
      en: 'Full access. This role cannot be edited or deleted.',
    },
    capabilities: ADMIN,
    landing: 'portal',
    position: 10,
    protected: true,
  },
  {
    key: 'admin',
    name: { fr: 'Administration', en: 'Administration' },
    description: {
      fr: 'Gère le travail, les clients, la facturation et l’équipe.',
      en: 'Manages work, customers, billing and the team.',
    },
    capabilities: ADMIN,
    landing: 'portal',
    position: 20,
    protected: false,
  },
  {
    key: 'dispatcher',
    name: { fr: 'Répartition', en: 'Dispatch' },
    description: {
      fr: 'Planifie et assigne le travail. Ne modifie pas la facturation.',
      en: 'Plans and assigns work. Does not edit billing.',
    },
    capabilities: DISPATCHER,
    landing: 'portal',
    position: 30,
    protected: false,
  },
  {
    key: 'accountant',
    name: { fr: 'Comptabilité', en: 'Accounting' },
    description: {
      fr: 'Soumissions, factures, paiements et coûts. Ne planifie pas le travail.',
      en: 'Quotes, invoices, payments and costs. Does not plan work.',
    },
    capabilities: ACCOUNTANT,
    landing: 'portal',
    position: 40,
    protected: false,
  },
  {
    key: 'lead_technician',
    name: { fr: 'Technicien senior', en: 'Lead technician' },
    description: {
      fr: 'Application mobile, plus les soumissions, les factures et l’horaire de la journée. Ne voit pas les prix de revient ni les profits.',
      en: 'Mobile app, plus quotes, invoices and the day’s schedule. Cannot see cost prices or profits.',
    },
    capabilities: LEAD_TECHNICIAN,
    landing: 'field',
    position: 50,
    protected: false,
  },
  {
    key: 'technician',
    name: { fr: 'Technicien', en: 'Technician' },
    description: {
      fr: 'Ses visites, ses heures, ses clients. Aucune donnée financière de l’entreprise.',
      en: 'Their visits, their hours, their customers. No company financials.',
    },
    capabilities: TECHNICIAN,
    landing: 'field',
    position: 60,
    protected: false,
  },
];

/**
 * Capabilities that cannot be handed out by someone who does not already hold them.
 *
 * Without this, an editable permission model is a privilege-escalation tool: anyone who can
 * edit roles can grant themselves everything, and `user.manage` becomes equivalent to owner.
 * The rule enforced server-side is simply that you may only grant what you have.
 */
export const PROTECTED_CAPABILITIES: readonly Capability[] = [
  'user.manage',
  'tenant.manage',
  'settings.write',
  'billing.cost.read',
  'time.rate.read',
  'payment.refund',
];

const CAPABILITY_SET: ReadonlySet<string> = new Set(CAPABILITIES);

/** True when every entry is a real capability. A typo must fail loudly, not grant nothing. */
export function areCapabilities(values: readonly string[]): values is Capability[] {
  return values.every((v) => CAPABILITY_SET.has(v));
}

export function unknownCapabilities(values: readonly string[]): string[] {
  return values.filter((v) => !CAPABILITY_SET.has(v));
}

/**
 * Whether `actor` may save a role holding `next`.
 *
 * Only the protected capabilities are restricted; the rest are ordinary editing. Returns the
 * offending capabilities so the interface can name them rather than just refusing.
 */
export function capabilitiesBeyond(
  actor: readonly Capability[],
  next: readonly Capability[],
): Capability[] {
  const held = new Set(actor);
  return next.filter((c) => PROTECTED_CAPABILITIES.includes(c) && !held.has(c));
}

/** True when this capability set only ever sees its own assigned work. */
export function scopedToOwnWork(capabilities: readonly Capability[]): boolean {
  return capabilities.includes('job.read.assigned') && !capabilities.includes('job.read.all');
}

export function can(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

/** True when this role only ever sees its own assigned work. Drives query-level filtering. */
export function isScopedToOwnWork(role: Role): boolean {
  return can(role, 'job.read.assigned') && !can(role, 'job.read.all');
}

/**
 * Fields stripped from API responses when the caller lacks `billing.cost.read`.
 *
 * Applied centrally in the serialisation layer rather than per endpoint, because "we
 * forgot to omit unitCost on one response" is exactly how commercially sensitive data
 * leaks. A technician must never receive what a part cost us.
 */
export const COST_FIELDS = ['unitCost', 'costTotal', 'unitProfit', 'totalProfit', 'margin', 'marginPercent'] as const;

/** Fields stripped when the caller lacks `time.rate.read`. Wages are private. */
export const RATE_FIELDS = ['hourlyRate', 'laborCost', 'costTotal'] as const;

/**
 * Which application a role lands in after signing in. Technicians go straight to the field
 * app; nobody has to choose.
 */
export function landingApp(role: Role): 'portal' | 'field' {
  return role === 'technician' ? 'field' : 'portal';
}
